import { CELL, GROUND } from '../core/config';
import { key, MATERIALS, type Building, type City, type Facility, type Place } from './city';
import type { Vec } from '../destruction/structure';
import { roadSegment } from './roads';

export interface Helipad {
  id: string;
  role: 'medical' | 'police';
  buildingId: number;
  facility: Facility;
  centre: Vec;
  handoff: Place;
  supports: number[];
}

/** Two finite service bases on clear parcels, connected to an existing public road. */
export function prepareHelipads(city: City): City {
  if (!city.regional || city.helipads) return city;
  city.helipads = [];
  const all = () => [...city.buildings, ...city.props!, ...city.bridges!, ...city.infrastructure!];
  let serial = Math.max(...all().map((b) => b.id)) + 1;
  for (const role of ['medical', 'police'] as const) {
    const facility = city.facilities!.find((f) => f.kind === (role === 'medical' ? 'hospital' : 'police'))!;
    const candidates: { x: number; z: number; entry: Vec; edge: number; distance: number }[] = [];
    for (const edge of city.roadGraph!.edges) {
      if (edge.bridgeId !== undefined || (edge.maxGrade ?? 0) > 0.03) continue;
      const a = city.roadGraph!.nodes[edge.from],
        b = city.roadGraph!.nodes[edge.to];
      const dx = b.x - a.x,
        dz = b.z - a.z,
        length = Math.hypot(dx, dz);
      if (length < 60) continue;
      for (const t of [0.3, 0.5, 0.7])
        for (const side of [-1, 1]) {
          const entry = { x: a.x + dx * t, y: GROUND + 0.2, z: a.z + dz * t };
          const x = entry.x - (dz / length) * side * (edge.width / 2 + 27),
            z = entry.z + (dx / length) * side * (edge.width / 2 + 27);
          if (Math.abs(x) > 700 || Math.abs(z) > 700) continue;
          const xmin = x - 13,
            xmax = x + 13,
            zmin = z - 13,
            zmax = z + 23;
          if (
            all().some((b) => xmin < b.x + b.w * CELL && xmax > b.x && zmin < b.z + b.d * CELL && zmax > b.z)
          )
            continue;
          // Also reserve the narrow pedestrian handoff connection, not just the pad square.
          const blocked = all().some((b) => {
            if (b.kind === 'road') return false;
            for (let step = 1; step < 12; step++) {
              const px = entry.x + ((x - entry.x) * step) / 12,
                pz = entry.z + ((z - entry.z) * step) / 12;
              if (
                px > b.x - 1.5 &&
                px < b.x + b.w * CELL + 1.5 &&
                pz > b.z - 1.5 &&
                pz < b.z + b.d * CELL + 1.5
              )
                return true;
            }
            return false;
          });
          if (!blocked)
            candidates.push({
              x,
              z,
              entry,
              edge: edge.id,
              distance: Math.hypot(x - facility.entry.x, z - facility.entry.z),
            });
        }
    }
    candidates.sort((a, b) => a.distance - b.distance);
    const spot = candidates[0];
    if (!spot) throw new Error(`No clear road-connected ${role} helipad parcel`);
    const b: Building = {
      id: serial++,
      name: role === 'medical' ? 'Riverside medical air support' : 'Civic observation air support',
      kind: 'road',
      category: 'prop',
      x: spot.x - 10,
      z: spot.z - 10,
      w: 16,
      d: 27,
      h: 1,
      color: 0x8fada2,
      accent: 0xe0dbc4,
      cells: [],
    };
    for (let x = 0; x < b.w; x++)
      for (let z = 0; z < b.d; z++) {
        const H = ((x === 5 || x === 10) && z >= 4 && z <= 12) || (z === 8 && x >= 5 && x <= 10);
        b.cells.push({
          id: b.id * 1e6 + key(x, 0, z),
          x,
          y: 0,
          z,
          material: 'concrete',
          role: 'slab',
          root: true,
          hp: MATERIALS.concrete.strength,
          color: H ? 0xf1e5c8 : 0x8fada2,
          bounds: { x: 0, y: 0, z: 0, w: 1, h: 0.12 / CELL, d: 1 },
        });
      }
    city.infrastructure!.push(b);
    const edge = city.roadGraph!.edges.find((e) => e.id === spot.edge)!;
    const handoff = { x: spot.x - 8, y: GROUND + 0.12, z: spot.z - 8 };
    const segments = Math.ceil(Math.hypot(handoff.x - spot.entry.x, handoff.z - spot.entry.z) / 28);
    for (let i = 0; i < segments; i++) {
      const point = (t: number) => ({
        x: spot.entry.x + (handoff.x - spot.entry.x) * t,
        y: spot.entry.y + (handoff.y - spot.entry.y) * t,
        z: spot.entry.z + (handoff.z - spot.entry.z) * t,
      });
      const access = roadSegment(
        serial++,
        { ...edge, width: 3 },
        point(i / segments),
        point((i + 1) / segments),
        1.5,
      );
      access.name = `${b.name} pedestrian and patient access ${i + 1}`;
      city.infrastructure!.push(access);
    }
    const place: Place = {
      id: `helipad:${role}:handoff`,
      kind: 'loading',
      name: `${role} air support patient handoff`,
      floor: 0,
      ...handoff,
      capacity: 1,
      duration: 12,
    };
    city.places!.push(place);
    city.helipads.push({
      id: `helipad:${role}`,
      role,
      buildingId: b.id,
      facility,
      centre: { x: spot.x, y: GROUND + 0.155, z: spot.z },
      handoff: place,
      supports: b.cells.filter((c) => (c.x === 6 || c.x === 9) && c.z >= 5 && c.z <= 10).map((c) => c.id),
    });
  }
  return city;
}
